import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChevronLeft, FileCheck2, Loader2, UploadCloud } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCriarFazenda } from '../api';
import { parseKmlFile, type TalhaoImport } from '../kml';
import { MiniMapaTalhoes } from './MiniMapaTalhoes';
import { Campo, SelectBox, Segmented } from './cadastro-ui';
import { CULTURAS, inputCls, UFS } from './cadastro-helpers';

function fmtMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
}

// Tela · Cadastro de nova fazenda: importa contorno (KML/KMZ) → talhões + preview,
// nome, tipo de área, município/UF, cultura. Fiel ao Figma "Cadastro · Nova fazenda".
export function NovaFazendaView({ cpf }: { cpf?: string | undefined }) {
  const navigate = useNavigate();
  const criar = useCriarFazenda();

  const [talhoes, setTalhoes] = useState<TalhaoImport[]>([]);
  const [arquivo, setArquivo] = useState<{ nome: string; bytes: number } | null>(null);
  const [nome, setNome] = useState('');
  const [posse, setPosse] = useState<'propria' | 'arrendada'>('propria');
  const [municipio, setMunicipio] = useState('');
  const [uf, setUf] = useState('RO');
  const [cultura, setCultura] = useState('Soja');
  const [erro, setErro] = useState<string | null>(null);

  function escolherArquivo() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz';
    input.onchange = () => {
      const f = input.files?.[0];
      if (!f) return;
      setErro(null);
      void parseKmlFile(f)
        .then((parsed) => {
          if (!parsed.length) {
            setErro('Nenhum polígono encontrado no arquivo.');
            return;
          }
          const ts = parsed.map((t, i) => {
            const kmlName = /^T\d+$/.test(t.numero) ? '' : t.numero;
            return { ...t, numero: String(i + 1), nome: kmlName || `Talhão ${i + 1}` };
          });
          setTalhoes(ts);
          setArquivo({ nome: f.name, bytes: f.size });
        })
        .catch(() => setErro('Não foi possível ler o arquivo KML/KMZ.'));
    };
    input.click();
  }

  function patch(i: number, p: Partial<TalhaoImport>) {
    setTalhoes((x) => x.map((t, idx) => (idx === i ? { ...t, ...p } : t)));
  }

  async function salvar() {
    if (!nome.trim() || !talhoes.length || criar.isPending) return;
    setErro(null);
    let res: { imovel_id?: number };
    try {
      res = (await criar.mutateAsync({
        nome: nome.trim(),
        proprietario_cpf: cpf ?? null,
        municipio: municipio.trim() || null,
        uf,
        tipo_posse: posse,
        cultura,
        talhoes: talhoes.map((t) => ({
          numero: t.numero,
          geojson: t.geojson,
          area_ha: t.area_ha,
          ...(t.nome ? { nome: t.nome } : {}),
        })),
      })) as { imovel_id?: number };
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar a fazenda.');
      return;
    }
    if (res.imovel_id) void navigate({ to: '/agronomia/fazendas/$id', params: { id: String(res.imovel_id) } });
    else window.history.back();
  }

  const totalHa = talhoes.reduce((s, t) => s + (t.area_ha || 0), 0);
  const podeSalvar = nome.trim().length > 1 && talhoes.length > 0 && !criar.isPending;

  return (
    <div className="relative flex h-full flex-col bg-bg">
      <header className="flex items-center gap-1 px-3 pt-4 pb-2 shrink-0">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text hover:bg-surface2"
          aria-label="Voltar"
        >
          <ChevronLeft size={26} strokeWidth={2} />
        </button>
        <h1 className="text-[1.35rem] font-bold leading-tight tracking-tight text-text">Nova fazenda</h1>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-40">
        <p className="pb-2 pt-1 text-[0.72rem] font-semibold uppercase tracking-wider text-text-2">
          Contorno da propriedade (KML / KMZ)
        </p>

        {arquivo ? (
          <div className="flex items-center gap-3 rounded-[14px] bg-[#dcefdf] px-3.5 py-3">
            <FileCheck2 size={22} strokeWidth={1.75} className="shrink-0 text-[#1d5a33]" />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-[0.9rem] font-semibold text-[#1d5a33]">{arquivo.nome}</span>
              <span className="text-[0.78rem] text-[#3c7a4e]">Importado · {fmtMb(arquivo.bytes)}</span>
            </div>
            <button
              type="button"
              onClick={escolherArquivo}
              className="shrink-0 rounded-full border border-[#1d5a33]/40 px-3.5 py-1.5 text-[0.8rem] font-semibold text-[#1d5a33]"
            >
              Trocar
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={escolherArquivo}
            className="flex w-full flex-col items-center justify-center gap-1.5 rounded-[14px] border-[1.5px] border-dashed border-[var(--agro-card-border)] bg-surface2 px-4 py-7 text-center"
          >
            <UploadCloud size={26} strokeWidth={1.5} className="text-accent" />
            <span className="text-[0.9rem] font-semibold text-text">Importar KML / KMZ</span>
            <span className="text-[0.78rem] text-text-2">Toque para selecionar o arquivo do contorno</span>
          </button>
        )}
        {erro && <p className="mt-2 text-[0.82rem] text-[#b3261e]">{erro}</p>}

        {talhoes.length > 0 && (
          <>
            <div className="mt-3 h-40 overflow-hidden rounded-[14px] bg-[#37431f]">
              <MiniMapaTalhoes talhoes={talhoes} />
            </div>

            <div className="flex items-center justify-between px-1 pb-2 pt-3">
              <span className="text-[0.9rem] font-semibold text-text">
                {talhoes.length} {talhoes.length === 1 ? 'talhão identificado' : 'talhões identificados'}
              </span>
              <span className="text-[0.9rem] font-semibold text-accent">
                {Math.round(totalHa).toLocaleString('pt-BR')} ha
              </span>
            </div>

            <div className="flex flex-col gap-2">
              {talhoes.map((t, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#fde3d5] text-[0.8rem] font-semibold text-accent">
                    {i + 1}
                  </span>
                  <input
                    className={cn(inputCls, 'flex-1 py-2.5')}
                    value={t.nome}
                    onChange={(e) => patch(i, { nome: e.target.value })}
                    placeholder={`Talhão ${i + 1}`}
                  />
                  <div className="relative w-[92px] shrink-0">
                    <input
                      className={cn(inputCls, 'py-2.5 pr-7')}
                      value={String(Math.round(t.area_ha || 0))}
                      onChange={(e) => patch(i, { area_ha: Number(e.target.value.replace(/\D/g, '')) || 0 })}
                      inputMode="numeric"
                      aria-label={`Área do talhão ${i + 1} em hectares`}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[0.8rem] text-text-muted">
                      ha
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="my-4 h-px bg-[var(--agro-card-border)]" />

        <div className="flex flex-col gap-4">
          <Campo label="Nome da fazenda">
            <input
              className={inputCls}
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex.: Fazenda Vilhena"
              autoCapitalize="words"
            />
          </Campo>

          <Campo label="Tipo de área">
            <Segmented
              value={posse}
              onChange={setPosse}
              options={[
                { value: 'propria', label: 'Própria' },
                { value: 'arrendada', label: 'Arrendamento' },
              ]}
            />
          </Campo>

          <div className="grid grid-cols-[1fr_96px] gap-3">
            <Campo label="Município">
              <input
                className={inputCls}
                value={municipio}
                onChange={(e) => setMunicipio(e.target.value)}
                placeholder="Vilhena"
                autoCapitalize="words"
              />
            </Campo>
            <Campo label="UF">
              <SelectBox value={uf} onChange={setUf} options={UFS} />
            </Campo>
          </div>

          <Campo label="Cultura principal">
            <SelectBox value={cultura} onChange={setCultura} options={CULTURAS} />
          </Campo>

          <button
            type="button"
            onClick={() => void salvar()}
            disabled={!podeSalvar}
            className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--accent)] py-3.5 text-[0.95rem] font-semibold text-[var(--on-solid)] disabled:opacity-50"
          >
            {criar.isPending && <Loader2 size={17} strokeWidth={2} className="animate-spin" />}
            Salvar fazenda
          </button>
        </div>
      </div>
    </div>
  );
}
